import Seo from '@/components/Seo';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, Phone, Loader2, Check } from 'lucide-react';
import { submitContact } from '@/lib/api';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiry_type: 'general',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitContact(formData);
      setIsSuccess(true);
      toast.success('Message sent successfully!');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        inquiry_type: 'general',
      });
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="contact-page">
      <Seo title="Contact" description="Contact Ink & Innovation Printworks and author Kavi Ocktiiv for orders, events, interviews, and publishing inquiries." />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-16"
        >
          <p className="overline text-gold mb-4">Get in Touch</p>
          <h1 className="text-4xl lg:text-5xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Contact Us
          </h1>
          <p className="text-white/60">
            Whether you're a reader with questions, a member of the press, or a writer interested 
            in submission opportunities, we'd love to hear from you.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="block text-white/40 text-xs uppercase tracking-wider mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-underline"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-white/40 text-xs uppercase tracking-wider mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-underline"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="block text-white/40 text-xs uppercase tracking-wider mb-2">
                    Inquiry Type
                  </label>
                  <Select
                    value={formData.inquiry_type}
                    onValueChange={(value) => setFormData({ ...formData, inquiry_type: value })}
                  >
                    <SelectTrigger className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus:ring-0" data-testid="contact-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#101010] border-white/10">
                      <SelectItem value="general" className="text-white/80 focus:bg-white/10 focus:text-white">
                        General Inquiry
                      </SelectItem>
                      <SelectItem value="press" className="text-white/80 focus:bg-white/10 focus:text-white">
                        Press & Media
                      </SelectItem>
                      <SelectItem value="business" className="text-white/80 focus:bg-white/10 focus:text-white">
                        Business Partnership
                      </SelectItem>
                      <SelectItem value="submissions" className="text-white/80 focus:bg-white/10 focus:text-white">
                        Manuscript Submissions
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-white/40 text-xs uppercase tracking-wider mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="input-underline"
                    data-testid="contact-subject"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/40 text-xs uppercase tracking-wider mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="input-underline resize-none"
                  data-testid="contact-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || isSuccess}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="contact-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : isSuccess ? (
                  <>
                    <Check size={18} />
                    Sent!
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="card-surface p-8">
              <Mail size={24} className="text-gold mb-4" />
              <h3 className="text-lg font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Email
              </h3>
              <p className="text-white/50 text-sm mb-4">For general inquiries:</p>
              <a href="mailto:inkandinnovationbooks@gmail.com" className="text-gold hover:text-gold/80 transition-colors break-all" data-testid="contact-email">
                inkandinnovationbooks@gmail.com
              </a>
            </div>

            <div className="card-surface p-8">
              <Phone size={24} className="text-gold mb-4" />
              <h3 className="text-lg font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Phone
              </h3>
              <p className="text-white/50 text-sm mb-4">Call or text us:</p>
              <a href="tel:+16783904403" className="text-gold hover:text-gold/80 transition-colors" data-testid="contact-phone">
                678-390-4403
              </a>
            </div>

            <div className="card-surface p-8">
              <h3 className="text-lg font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Response Times
              </h3>
              <ul className="space-y-2 text-white/50 text-sm">
                <li className="flex justify-between">
                  <span>General inquiries:</span>
                  <span className="mono text-white/70">2-3 days</span>
                </li>
                <li className="flex justify-between">
                  <span>Press requests:</span>
                  <span className="mono text-white/70">24 hours</span>
                </li>
                <li className="flex justify-between">
                  <span>Submissions:</span>
                  <span className="mono text-white/70">4-6 weeks</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
